import React from 'react';
import styled from 'styled-components';
import axios from 'axios';

import { Router } from './Router';
import { Alert, Stack } from '@mui/material';
import { DocsSections } from '@components/Docs/types';
import { NAVBAR_TOP_HEIGHT, SIDENAV_WIDTH } from '@components/Docs';
import { useQuery } from '@tanstack/react-query';
import { getDocumentation } from '@requests/documentation';
import { Loader } from '@common/hooks/useLoader';

const Container = styled.div`
  background-color: ${({ theme }) => theme.background.standard};
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.line.light};
  border-radius: 8px;

  margin: 0 28px;
  margin-bottom: 28px;

  max-height: calc(100vh - ${NAVBAR_TOP_HEIGHT} - 28px);

  min-width: ${SIDENAV_WIDTH};
  max-width: ${SIDENAV_WIDTH};

  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    background: ${({ theme }) => theme.background.standard} !important;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 8px;
    background: ${({ theme }) => theme.text.subtext};
  }
`;

interface Props {
  router: DocsSections[];
  active: string | null;
  setActive: React.Dispatch<string | null>;
}

export const DocsSideNav: React.FC<Props> = ({ active, setActive }) => {
  const {
    data: router = [],
    error,
    isLoading,
  } = useQuery({
    queryKey: ['docs'],
    queryFn: getDocumentation,
  });

  let errorMessage = '';
  if (axios.isAxiosError(error)) errorMessage = error.response?.data?.error || error.message;
  else errorMessage = error?.message || '';

  if (error) {
    return (
      <Container>
        <Alert sx={{ maxWidth: SIDENAV_WIDTH }} severity='error'>
          {errorMessage || 'Unable to load sidebar'}
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container>
        <Stack mt={3} display='flex' alignItems='center' justifyContent='center' spacing={2}>
          <Loader />
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      {router.map((route, index) => (
        <Router
          key={index}
          name={route.name}
          active={active}
          setActive={setActive}
          links={route.subsections}
          index={index}
          id={route.id}
        />
      ))}
    </Container>
  );
};
